import { useCallback, useEffect, useRef, useState } from 'react';
import { BackgroundProcessor } from '@livekit/track-processors';
import { toast } from 'react-toastify';
import { BLUR_RADIUS, getSegmenterAssetPaths } from '../../backgroundConfig';
import { loadStoredBackgroundMode, storeBackgroundMode } from '../../backgroundPreference';
import { getSupportsBackgroundProcessors } from '../../getSupportsBackgroundProcessors';
/**
 * Процессор фона читает кадры из скрытого <video>. На выключенной камере кадров
 * нет, и его renderLoop падает с `InvalidStateError: Video element has no video
 * frame` на каждом кадре: сотни исключений в секунду, каждое из которых
 * PostHog отправляет на сервер, — вкладка перестаёт отвечать и вход в звонок
 * не доходит до конца.
 */
function isTrackRenderable(track) {
    if (!track)
        return false;
    const mediaStreamTrack = track.mediaStreamTrack;
    return !track.isMuted && !!mediaStreamTrack && mediaStreamTrack.enabled && mediaStreamTrack.readyState === 'live';
}
export function useLiveKitVirtualBackground({ localVideoTrackRef, localVideoTrack, isVideoMuted, }) {
    const [backgroundMode, setBackgroundMode] = useState('none');
    const backgroundModeRef = useRef('none');
    const backgroundProcessorRef = useRef(null);
    const selectedImagePath = useRef(null);
    const backgroundAssetsPrefetched = useRef(false);
    const backgroundRefreshInProgress = useRef(false);
    const backgroundRestoredRef = useRef(false);
    const backgroundDelegateRef = useRef('GPU');
    const blurRadiusRef = useRef(BLUR_RADIUS);
    /** Фон, снятый на время выключенной камеры: вернём его, когда камера включится. */
    const suspendedBackgroundRef = useRef(null);
    useEffect(() => {
        backgroundModeRef.current = backgroundMode;
    }, [backgroundMode]);
    /**
     * Один процессор на оба режима: blur и картинка отличаются только опциями
     * трансформера, поэтому переключение между ними не требует пересоздания
     * WebGL-контекста и перезагрузки модели (см. switchBackgroundOptions).
     */
    const createBackgroundProcessor = useCallback((mode, imagePath, delegate) => BackgroundProcessor({
        ...(mode === 'blur' ? { blurRadius: blurRadiusRef.current } : { imagePath }),
        segmenterOptions: { delegate },
        assetPaths: getSegmenterAssetPaths(),
    }, 'meet-background'), []);
    //включение виртуального фона
    const enableBackground = useCallback(async (mode, imagePath, options) => {
        if (!localVideoTrackRef.current)
            return;
        if (!isTrackRenderable(localVideoTrackRef.current)) {
            if (!options?.silent) {
                toast.info('Включите камеру, чтобы применить фон');
            }
            return;
        }
        if (mode === 'image' && !imagePath) {
            console.error('Background failed: image path required');
            toast.error('Не удалось применить фон');
            return;
        }
        /**
         * GPU-делегат MediaPipe доступен не на всех связках драйвер/браузер, и при
         * отказе init процессора падает целиком. Раньше это выглядело как «фон
         * просто не включается»; теперь один раз пробуем CPU — он медленнее, но
         * работает почти везде.
         */
        const delegates = backgroundDelegateRef.current === 'CPU' ? ['CPU'] : ['GPU', 'CPU'];
        let lastError = null;
        for (const delegate of delegates) {
            try {
                const processor = createBackgroundProcessor(mode, imagePath, delegate);
                await localVideoTrackRef.current.setProcessor(processor);
                backgroundProcessorRef.current = processor;
                backgroundDelegateRef.current = delegate;
                selectedImagePath.current = mode === 'image' ? (imagePath ?? null) : null;
                backgroundModeRef.current = mode;
                setBackgroundMode(mode);
                storeBackgroundMode(mode, selectedImagePath.current);
                if (!options?.silent) {
                    toast.success(mode === 'blur' ? 'Размытие фона включено' : 'Виртуальный фон включен');
                }
                return;
            }
            catch (error) {
                lastError = error;
                console.warn(`Background init failed on ${delegate} delegate`, error);
                await localVideoTrackRef.current?.stopProcessor().catch(() => undefined);
            }
        }
        console.error('Background failed:', lastError);
        if (!options?.silent) {
            toast.error('Не удалось применить фон');
        }
    }, [createBackgroundProcessor, localVideoTrackRef]);
    /**
     * Снять процессор, не теряя выбранный режим. LocalVideoTrack.mute() физически
     * останавливает MediaStreamTrack, чтобы погас индикатор камеры, — процессор
     * остаётся на мёртвом элементе и сыплет InvalidStateError на каждом кадре.
     */
    const suspendBackground = useCallback(async () => {
        const mode = backgroundModeRef.current;
        if (mode === 'none' || !localVideoTrackRef.current)
            return;
        suspendedBackgroundRef.current = { mode, imagePath: selectedImagePath.current ?? undefined };
        backgroundProcessorRef.current = null;
        try {
            await localVideoTrackRef.current.stopProcessor();
        }
        catch (error) {
            console.warn('Failed to stop background processor on camera mute', error);
        }
    }, [localVideoTrackRef]);
    //отключение виртуального фона
    const disableBackground = useCallback(async () => {
        suspendedBackgroundRef.current = null;
        if (!localVideoTrackRef.current || backgroundModeRef.current === 'none')
            return;
        try {
            await localVideoTrackRef.current.stopProcessor();
            backgroundProcessorRef.current = null;
            selectedImagePath.current = null;
            setBackgroundMode('none');
            backgroundModeRef.current = 'none';
            storeBackgroundMode('none');
        }
        catch (error) {
            console.error('Disable background failed:', error);
        }
    }, [localVideoTrackRef]);
    /**
     * Смена режима на уже запущенном процессоре. Полный stop/start перезагружает
     * модель сегментации и пересоздаёт WebGL-контекст — это секунда-две фриза
     * локального видео на каждое переключение фона.
     */
    const switchBackgroundOptions = useCallback(async (mode, imagePath) => {
        if (!backgroundProcessorRef.current)
            return false;
        try {
            await backgroundProcessorRef.current.updateTransformerOptions(mode === 'blur' ? { blurRadius: blurRadiusRef.current, imagePath: undefined } : { imagePath });
            selectedImagePath.current = mode === 'image' ? (imagePath ?? null) : null;
            backgroundModeRef.current = mode;
            setBackgroundMode(mode);
            storeBackgroundMode(mode, selectedImagePath.current);
            return true;
        }
        catch (error) {
            console.warn('Failed to switch background in place, falling back to restart', error);
            return false;
        }
    }, []);
    //выбор фона
    const toggleBackground = useCallback(async (mode, imagePath, options) => {
        if (mode === 'none') {
            await disableBackground();
            return;
        }
        const isSameMode = backgroundMode === mode;
        let isSameImage = true;
        if (mode === 'image') {
            if (!imagePath)
                throw new Error('Image path required for image mode');
            isSameImage = selectedImagePath.current === imagePath;
        }
        // Повторный клик по активному режиму — это выключение.
        if (isSameMode && (mode !== 'image' || isSameImage)) {
            await disableBackground();
            return;
        }
        // Процессор уже крутится — меняем опции на лету, без перезагрузки модели.
        if (backgroundMode !== 'none' && (await switchBackgroundOptions(mode, imagePath))) {
            return;
        }
        if (backgroundMode !== 'none') {
            await disableBackground();
        }
        await enableBackground(mode, imagePath, options);
    }, [backgroundMode, disableBackground, enableBackground, switchBackgroundOptions]);
    const refreshBackgroundProcessor = useCallback(async () => {
        if (backgroundMode === 'none' || !localVideoTrackRef.current || backgroundRefreshInProgress.current) {
            return;
        }
        const modeToRestore = backgroundMode;
        const imagePath = selectedImagePath.current ?? undefined;
        if (modeToRestore === 'image' && !imagePath) {
            return;
        }
        backgroundRefreshInProgress.current = true;
        try {
            await localVideoTrackRef.current.stopProcessor();
            backgroundProcessorRef.current = null;
            await enableBackground(modeToRestore, imagePath, { silent: true });
        }
        catch (error) {
            console.error('Failed to refresh background processor after visibility change', error);
        }
        finally {
            backgroundRefreshInProgress.current = false;
        }
    }, [backgroundMode, enableBackground, localVideoTrackRef]);
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshBackgroundProcessor();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshBackgroundProcessor]);
    useEffect(() => {
        if (backgroundAssetsPrefetched.current || typeof window === 'undefined' || !getSupportsBackgroundProcessors()) {
            return;
        }
        const controller = new AbortController();
        const assetUrls = [getSegmenterAssetPaths().modelAssetPath];
        const preloadAssets = async () => {
            try {
                await Promise.all(assetUrls.map((url) => fetch(url, {
                    signal: controller.signal,
                    cache: 'force-cache',
                })));
                backgroundAssetsPrefetched.current = true;
            }
            catch (error) {
                if (controller.signal.aborted)
                    return;
                console.warn('Failed to preload background assets', error);
            }
        };
        preloadAssets();
        return () => controller.abort();
    }, []);
    // Камеру выключили — снимаем процессор по 'ended' исходного трека (это происходит
    // внутри mute(), раньше, чем обновится isVideoMuted). Включили — возвращаем фон.
    useEffect(() => {
        if (!localVideoTrack)
            return;
        if (!isVideoMuted) {
            const suspended = suspendedBackgroundRef.current;
            if (suspended && isTrackRenderable(localVideoTrack)) {
                suspendedBackgroundRef.current = null;
                enableBackground(suspended.mode, suspended.imagePath, { silent: true });
            }
        }
        const mediaStreamTrack = localVideoTrack.mediaStreamTrack;
        if (!mediaStreamTrack)
            return;
        const handleEnded = () => {
            void suspendBackground();
        };
        mediaStreamTrack.addEventListener('ended', handleEnded);
        return () => {
            mediaStreamTrack.removeEventListener('ended', handleEnded);
        };
    }, [enableBackground, isVideoMuted, localVideoTrack, suspendBackground]);
    // Восстановление выбранного в прошлый раз фона — один раз за сессию, как только
    // появился локальный трек. Если вошли с выключенной камерой, ждём её включения:
    // на выключенной камере процессор запускать нельзя (см. isTrackRenderable).
    useEffect(() => {
        if (backgroundRestoredRef.current || !localVideoTrack || !getSupportsBackgroundProcessors()) {
            return;
        }
        if (isVideoMuted || !isTrackRenderable(localVideoTrack))
            return;
        const stored = loadStoredBackgroundMode();
        if (!stored)
            return;
        backgroundRestoredRef.current = true;
        enableBackground(stored.mode, stored.imagePath, { silent: true });
    }, [enableBackground, isVideoMuted, localVideoTrack]);
    return {
        backgroundBlur: backgroundMode !== 'none',
        backgroundMode,
        backgroundModeRef,
        isBackgroundSupported: getSupportsBackgroundProcessors(),
        disableBackground,
        toggleBackground,
    };
}
